import * as React from 'react'
import { parseQueryString } from '../utils'
import { Spinner, CancelButton, Errors } from '../@components'
import { apiClient } from "../services/appClient"
import appSettings from '../appSettings'
import { IError } from '../services/errorService'

const NameOnly = "UserName";
const NameAndPassword = "UserNameAndPassword";

enum RestorePasswordPageStages {
    Validation,
    Form,
    Final,
    ResendLink,
    ResendLinkFinal,
}

interface IRestorePageState {
    mode: string;
    emailOrUserName: string;
    isEmailOrUserNameValid: boolean;
    stage: RestorePasswordPageStages;
    error?: IError;
    request: boolean;
}

export class RestorePage extends React.Component<{}, IRestorePageState> {
    constructor(props: {}) {
        super(props);

        var params = parseQueryString(location.search);
        this.state = {
            mode: NameAndPassword,
            emailOrUserName: params.username || "",
            stage: RestorePasswordPageStages.Form,
            isEmailOrUserNameValid: true,
            error: undefined,
            request: false
        }
        this.onModeChanged = this.onModeChanged.bind(this);
        this.onEmailOrUserNameChanged = this.onEmailOrUserNameChanged.bind(this);
        this.onCreateRequest = this.onCreateRequest.bind(this);
        this.onResetActivationLink = this.onResetActivationLink.bind(this);
    }

    public render() {
        return (
            <form className="form-horizontal">
                {this.renderInternal()}
            </form>
        );
    }

    public componentDidMount() {
        if (this.state.stage == RestorePasswordPageStages.Form && this.state.emailOrUserName != "") {
            this.validateUser(this.state.emailOrUserName);
        }
    }

    private renderInternal() {
        switch (this.state.stage) {
            case RestorePasswordPageStages.Form:
                return this.renderForm();
            case RestorePasswordPageStages.Validation:
                return this.renderValidation();
            case RestorePasswordPageStages.Final:
                return this.renderFinal();
            case RestorePasswordPageStages.ResendLink:
                return this.renderResetActivationLink();
            case RestorePasswordPageStages.ResendLinkFinal:
                return this.renderResetActivationLinkFinal();
        }
    }

    private renderForm() {
        const idTitle = this.state.mode === NameAndPassword ? "User Name/Email" : "Email";
        const idPlaceholder = this.state.mode === NameAndPassword ? "Enter your User Name/Email" : "Enter your email";
        const idClasses = "form-group" + (this.state.isEmailOrUserNameValid ? "" : " has-error");

        return (
            <React.Fragment>
                <div className="page-header">
                    <h1>I Cannot Remember My User Name Or Password</h1>
                </div>
                <div>You have requested a password reset or are required to reset your password for security purposes.</div>
                <div>Select the option that best describes your situation:</div>
                <Errors error={this.state.error}/>
                <div className="radio">
                    <label><input name="mode" type="radio" value={NameAndPassword} checked={this.state.mode === NameAndPassword} onChange={this.onModeChanged}/>I need to retrieve my User Name and reset my password</label>
                </div>
                <div className="radio">
                    <label><input name="mode" type="radio" value={NameOnly} checked={this.state.mode === NameOnly} onChange={this.onModeChanged}/>I need to retrieve my User Name only</label>
                </div>
                <div className={idClasses}>
                    <label htmlFor="emailOrUserName" className="col-sm-2 control-label">{idTitle}:<span className="text-required">*</span></label>
                    <div className="col-sm-5">
                        <input type="text" className="form-control" id="emailOrUserName" value={this.state.emailOrUserName} placeholder={idPlaceholder} onChange={this.onEmailOrUserNameChanged}/>
                    </div>
                </div>
                <div className="form-group">
                    <div className="col-sm-offset-2 col-sm-10">
                        <button type="submit" className="btn btn-wmata" onClick={this.onCreateRequest} disabled={this.state.request}>Submit</button>
                        &nbsp;
                        <CancelButton />
                        <Spinner show={this.state.request}/>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private renderValidation() {
        return (
            <React.Fragment>
                <Spinner show={this.state.request}/>
            </React.Fragment>
        );
    }

    private renderFinal() {
        if (this.state.mode === NameAndPassword)
        {
            return (
                <React.Fragment>
                    <div className="page-header">
                        <h1>Password Reset Link Was Sent</h1>
                    </div>
                    <p>Your Password Reset Link Was Sent</p>
                    <p>
                        A password reset link was sent to your email address.
                        If you have not received it within ten minutes, please call the SmarTrip® Regional Customer Service Center at {appSettings.SmarTripRegionalCustomerServiceCenterPhone}.
                    </p>
                    <p>
                        <CancelButton />
                    </p>
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    <div className="page-header">
                        <h1>User Name Information Was Sent</h1>
                    </div>
                    <p>Your User Name Information Was Sent</p>
                    <p>
                        Your User Name was sent to your email address.
                        If you have not received it within ten minutes, please call the SmarTrip® Regional Customer Service Center at {appSettings.SmarTripRegionalCustomerServiceCenterPhone}.
                    </p>
                </React.Fragment>
            );
        }
    }

    private renderResetActivationLink() {
        return (
            <React.Fragment>
                <div className="page-header">
                    <h1>Your account has not yet been activated</h1>
                </div>
                <p>
                    Please check your e-mail and click a link to activate your account.<br />
                    Didn't receive an activation email? <button type="button" onClick={this.onResetActivationLink}>Click here to generate a new</button>
                </p>
                <Errors error={this.state.error} />
                <p>
                    <CancelButton />
                    <Spinner show={this.state.request} />
                </p>
            </React.Fragment>
        );
    }

    private renderResetActivationLinkFinal() {
        return (
            <React.Fragment>
                <div className="page-header">
                    <h1>Activation Link Sent</h1>
                </div>
                <div>Activation link has been sent.</div>
            </React.Fragment>
        );
    }

    private onModeChanged(e: any) {
        this.setState({
            mode: e.currentTarget.value
        });
    }

    private onEmailOrUserNameChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        this.setState({
            emailOrUserName: value,
            isEmailOrUserNameValid: true
        });
    }

    private onCreateRequest(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        if (!this.validate()) {
            return;
        }

        this.setState({
            request: true
        });

        var data = {
            emailOrUserName: this.state.emailOrUserName,
            retrieveOption: this.state.mode === NameAndPassword ? 0 : 1,
        }

        apiClient.createRecoveryRequest(data)
            .then(() => {
                this.setState({
                    error: undefined,
                    stage: RestorePasswordPageStages.Final,
                    request: false
                });
            })
            .catch(error => {
                if (error.errorKey === "errors.account.pending.activation") {
                    this.setState({
                        error: undefined,
                        stage: RestorePasswordPageStages.ResendLink,
                        request: false
                    });
                } else {
                    this.setState({
                        error: error,
                        request: false
                    });
                }
            });
    }

    private onResetActivationLink(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        var data = {
            emailOrUserName: this.state.emailOrUserName,
            retrieveOption: this.state.mode === NameAndPassword ? 0 : 1,
        }

        apiClient.resendActivationLink(data)
            .then(() => {
                this.setState({
                    error: undefined,
                    stage: RestorePasswordPageStages.ResendLinkFinal,
                    request: false
                });
            })
            .catch(error => {
                this.setState({
                    error: error,
                    request: false
                });
            });
    }

    private validateUser(emailOrUserName: string) {
        this.setState({
            stage: RestorePasswordPageStages.Validation,
            request: true
        });

        apiClient.getPatronStatus(emailOrUserName)
            .then(() => {
                this.setState({
                    error: undefined,
                    stage: RestorePasswordPageStages.Form,
                    request: false
                });
            })
            .catch(error => {
                this.setState({
                    stage: RestorePasswordPageStages.Form,
                    request: false
                });
                if (error.errorKey === "errors.account.pending.activation") {
                    this.setState({
                        error: undefined,
                        stage: RestorePasswordPageStages.ResendLink
                    });
                }
            });
    }

    private validate(): boolean {
        if (!this.state.emailOrUserName) {
            this.setState({
                isEmailOrUserNameValid: false
            });
            return false;
        }

        if (this.state.mode == NameOnly && !/^[A-Za-z0-9_%+-]+(\.[A-Za-z0-9_%+-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,4}$/.test(this.state.emailOrUserName)) {
            this.setState({
                isEmailOrUserNameValid: false
            });
            return false;
        }

        return true;
    }
}


// WEBPACK FOOTER //
// ./src/pages/restore.tsx